import { Card, Typography } from '@globalmed/atlas-ui';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  generatePath,
  Outlet,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import style from './GuestLayout.module.css';
import { chatApi } from '@/API';
import { Loader } from '@/components/Loader';
import { useBrandingColors } from '@/hooks/useBrandingColors';
import { useCustomFavicon } from '@/hooks/useCustomFavicon';
import { SocketLayout } from '@/layouts/SocketLayout';
import { useAppointmentsStore } from '@/stores/appointments';
import { useInstitutionsStore } from '@/stores/institutions';
import { useListStore } from '@/stores/list';
import { useUsersStore } from '@/stores/users';
import { ERouterNames, EUserRoles, TNullable } from '@/types';
import { EChatEvent } from '@/types/chats';
import { LOCAL_STORAGE_KEYS } from '@/utils/constants';
import { nameFormat } from '@/utils/valuesFormat';
import { useSurveyStore } from '@/stores/survey';

export const GuestLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<TNullable<string>>(null);
  const [loading, setLoading] = useState(true);
  useBrandingColors();
  useCustomFavicon();

  const { userData, fetchUserData, getUsers } = useUsersStore((state) => ({
    userData: state.userData,
    fetchUserData: state.fetchUserData,
    getUsers: state.getUsers,
  }));
  const { getAppointments, getAppointment } = useAppointmentsStore((state) => ({
    getAppointments: state.getAppointments,
    getAppointment: state.getAppointment,
  }));
  const currentInstitution = useInstitutionsStore(
    (state) => state.currentInstitution,
  );
  const getCountries = useListStore((state) => state.getCountries);
  const getSpecialties = useSurveyStore((state) => state.getSpecialties);

  useEffect(() => {
    const initGuestSession = async () => {
      try {
        const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        if (!token) {
          setError('No access token found');
          navigate(ERouterNames.PAGE_NOT_FOUND);
          setLoading(false);
          return;
        }

        if (!userData) await fetchUserData();
      } catch (err) {
        console.error('Error initializing guest session:', err);
        navigate(ERouterNames.PAGE_NOT_FOUND);
        setLoading(false);
        return;
      }
    };

    void initGuestSession();
  }, [navigate, fetchUserData, userData]);

  const institutionId = userData?.userInstitutionRoles[0]?.institutionId;
  useEffect(() => {
    const loadAppointmentsAndNavigate = async () => {
      if (!institutionId) return;
      try {
        await Promise.all([
          getAppointments({ institutionId, role: EUserRoles.GUEST }),
          getUsers(institutionId),
          getCountries(),
          getSpecialties(),
        ]);

        const chatsResponse = await chatApi.getChats();
        const chats = chatsResponse.data.data;

        if (!chats.length) return;

        const sortedChats = [...chats].sort(
          (a, b) =>
            dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf(),
        );

        const latestChat = sortedChats[0];

        if (!latestChat?.appointment_id) return;

        const appointment = await getAppointment(latestChat.appointment_id);

        const endEvent = latestChat.events.find(
          (event) => event.type === EChatEvent.ENDED,
        );

        if (endEvent) {
          const provider = endEvent.user;

          const providerName = provider
            ? nameFormat(provider.first_name, provider.last_name)
            : t('provider');

          setError(
            t(
              'encounter.this-meeting-is-no-longer-available-please-contact-providername-if-you-have-further-questions',
              { providerName },
            ),
          );
          return;
        }

        if (
          currentInstitution?.configuration?.waitingRoomOption &&
          appointment?.waitingRoom?.id
        ) {
          const waitingRoomUrl = generatePath(
            `${ERouterNames.GUEST}/${ERouterNames.WAITING_ROOM}`,
            { waitingRoomId: appointment.waitingRoom.id },
          );
          if (location.pathname === ERouterNames.GUEST) {
            navigate(waitingRoomUrl);
          }
          return;
        }

        const redirectUrls: Record<string, string> = {
          chat: generatePath(`${ERouterNames.GUEST}/${ERouterNames.CHAT}`, {
            chatId: latestChat.id,
          }),
          video: generatePath(
            `${ERouterNames.GUEST}/${ERouterNames.ENCOUNTER}`,
            {
              encounterId: appointment.encounterId,
            },
          ),
        };

        const redirectUrl = redirectUrls[appointment.communicationChannel];

        if (!redirectUrl) {
          setError('Unknown communication channel or missing encounter ID');
          navigate(ERouterNames.PAGE_NOT_FOUND);
          return;
        }

        if (location.pathname === ERouterNames.GUEST) {
          navigate(redirectUrl);
          return;
        }
      } catch (err) {
        console.error('Error loading appointments:', err);
      } finally {
        setLoading(false);
      }
    };

    void loadAppointmentsAndNavigate();
  }, [
    institutionId,
    getAppointments,
    getAppointment,
    getUsers,
    getCountries,
    navigate,
    currentInstitution,
    t,
    location.pathname,
    getSpecialties,
  ]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className={style.errorWrapper}>
        <Card variant={'outlined'}>
          <Typography as="h4" textAlign={'center'}>
            {error}
          </Typography>
        </Card>
      </div>
    );
  }

  return (
    <SocketLayout>
      <main className={style.wrapper}>
        <Outlet />
      </main>
    </SocketLayout>
  );
};
